/**
 * Terreng fra Kartverket DTM1.
 *
 * Flisene bygges offline av `scripts/build-terrain-tiles.py` og ligger som
 * statiske PNG-er under `public/terrain/`. Ingen ekstern terrengtjeneste,
 * ingen API-nøkkel, ingen kjøretidskonvertering.
 *
 * Hvorfor dette ikke gjør opplevelsen tyngre:
 *
 *   - Fliser er 512 px og stopper på z13 (~4,8 m på bakken her nord).
 *     Zoomer vi tettere, strekker MapLibre samme nett videre i stedet for å
 *     be om fliser som ikke finnes. Hele settet er 181 fliser / ~11 MB, og
 *     ett kamerautsnitt laster typisk 6-12 av dem.
 *   - `bounds` gjør at MapLibre aldri ber om fliser utenfor Kartverket-
 *     bestillingen. Uten det ville kanten gitt en foss av 404-er.
 *   - Kostnaden ved terreng er render-passet, ikke dataene. Derfor står
 *     hillshade default AV: det er et ekstra pass, og kartet leser allerede
 *     terrenget gjennom 3D-pitchen.
 */

export const TERRAIN_SOURCE_ID = 'kartverket-dtm';
export const HILLSHADE_LAYER_ID = 'kartverket-hillshade';

/**
 * Dekning for flissettet, fra Kartverkets klippepolygon.
 * Holdes i synk med `public/terrain/meta.json`.
 * [west, south, east, north]
 */
export const TERRAIN_BOUNDS = [10.32715, 59.83242, 10.92307, 59.99285];

/**
 * Vertikal overdrivelse.
 *
 * 1.0 = ekte høyder. Men korridoren Bærums Verk–Vika har 336 m relieff
 * fordelt på 22 km – 1,5 %. I overview-kameraet (zoom 11.4) er den høyeste
 * åsen da rundt 1 % av skjermbredden, og kartet leses som flatt selv om
 * høydene er helt korrekte. Derfor 1.8 som default: nok til at åsene bærer
 * fortellingen om at bussen klatrer til 207 m mens bilen går rundt.
 *
 * Sett den til 1.0 hvis grafikken skal kunne måles på.
 *
 * MERK: `map.queryTerrainElevation()` returnerer overdrevet høyde, og
 * modellene bruker nettopp den – så modeller og terreng følger hverandre
 * uansett hva denne settes til.
 */
export const TERRAIN_EXAGGERATION = 1.8;

/** Slå på hillshade. Ekstra render-pass – kun hvis det trengs visuelt. */
export const HILLSHADE_ENABLED = false;

/**
 * Terrengkilden som skal inn i `sources` i stiobjektet.
 */
export function terrainSource(baseUrl = '/') {
  return {
    type: 'raster-dem',
    tiles: [`${baseUrl}terrain/{z}/{x}/{y}.png`],
    // Mapbox-koding: høyde = -10000 + (R*65536 + G*256 + B) * 0.1
    encoding: 'mapbox',
    tileSize: 512,
    minzoom: 10,
    maxzoom: 13,
    bounds: TERRAIN_BOUNDS,
    attribution:
      '<a href="https://www.kartverket.no/">Kartverket</a> DTM 1 m (CC BY 4.0)',
  };
}

/**
 * Slår på terrenget. Kalles etter at stilen er lastet.
 * @param {import('maplibre-gl').Map} map
 * @param {string} [beforeLayerId] lag hillshade under dette laget
 */
export function enableTerrain(map, beforeLayerId) {
  map.setTerrain({
    source: TERRAIN_SOURCE_ID,
    exaggeration: TERRAIN_EXAGGERATION,
  });

  if (HILLSHADE_ENABLED && !map.getLayer(HILLSHADE_LAYER_ID)) {
    map.addLayer(
      {
        id: HILLSHADE_LAYER_ID,
        type: 'hillshade',
        source: TERRAIN_SOURCE_ID,
        paint: {
          // Matcher morgenlyset i style.js: lav sol fra øst.
          'hillshade-illumination-direction': 90,
          'hillshade-illumination-anchor': 'map',
          'hillshade-exaggeration': 0.35,
          'hillshade-shadow-color': '#8a7f6a',
          'hillshade-highlight-color': '#f2e8d4',
          'hillshade-accent-color': '#b5a992',
        },
      },
      beforeLayerId,
    );
  }
}

/**
 * Bakkehøyde i meter på et punkt, eller `null` til DEM-flisene som dekker
 * punktet er lastet.
 *
 * MapLibre svarer `null` (ikke 0) før flisen finnes, og det skillet er
 * viktig: en modell som tror bakken ligger på 0 mens den egentlig ligger
 * på 105 m, graver seg ned i åsen. Derfor sjekker kallerne mot null og
 * prøver igjen neste frame i stedet for å ta svaret for gitt.
 *
 * @returns {number|null}
 */
export function groundElevation(map, lngLat) {
  if (!map.getTerrain()) return null;
  const h = map.queryTerrainElevation(lngLat);
  return Number.isFinite(h) ? h : null;
}
